import { useMemo } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatQty } from '../lib/quantity';

export type MonthDef = {
  index: number;
  label: string;
};

export type YearSeries = {
  year: number;
  values: number[];
};

const YEAR_COLORS = ['#2563eb', '#0f766e', '#f97316'];

type ChartRow = {
  month: string;
  [key: string]: string | number;
};

type TooltipEntry = {
  dataKey?: string | number;
  color?: string;
  value?: number | string;
  name?: string;
};

type TooltipColorMap = Record<string, string>;

function buildRows(series: YearSeries[], months: readonly MonthDef[]): ChartRow[] {
  return months.map((month, monthIndex) => {
    const row: ChartRow = { month: month.label };
    series.forEach((entry) => {
      row[String(entry.year)] = entry.values[monthIndex] ?? 0;
    });
    return row;
  });
}

function YearLegend({ series }: { series: YearSeries[] }) {
  return (
    <div className="flex flex-wrap gap-3">
      {series.map((entry, index) => (
        <div key={entry.year} className="flex items-center gap-2 text-sm text-slate-600">
          <span className="h-3 w-3 rounded-full" style={{ backgroundColor: YEAR_COLORS[index] }} />
          <span>{entry.year}</span>
        </div>
      ))}
    </div>
  );
}

function MatrixLegend({
  series,
  months,
  suffix,
}: {
  series: YearSeries[];
  months: readonly MonthDef[];
  suffix?: string;
}) {
  return (
    <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/80">
      <div
        className="grid text-[11px]"
        style={{ gridTemplateColumns: `84px repeat(${months.length}, minmax(0, 1fr))` }}
      >
        <div className="border-b border-slate-200 px-2 py-2 font-medium text-slate-500">År</div>
        {months.map((month) => (
          <div key={month.index} className="border-b border-slate-200 px-2 py-2 font-medium text-slate-500">
            {month.label}
          </div>
        ))}
        {series.map((entry, index) => (
          <div key={entry.year} className="contents">
            <div className="border-b border-slate-200 px-2 py-2 font-medium text-slate-700">
              <span className="mr-2 inline-block h-2.5 w-2.5 rounded-full align-middle" style={{ backgroundColor: YEAR_COLORS[index] }} />
              {entry.year}
            </div>
            {months.map((month, monthIndex) => (
              <div key={`${entry.year}-${month.index}`} className="border-b border-slate-200 px-2 py-2 text-slate-900">
                {formatQty(entry.values[monthIndex] ?? 0)}{suffix ?? ''}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ChartTooltip({
  active,
  payload,
  label,
  suffix,
  colors,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  suffix?: string;
  colors?: TooltipColorMap;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg">
      <div className="text-xs font-semibold text-slate-900">{label}</div>
      <div className="mt-1 space-y-1 text-xs text-slate-600">
        {payload.map((entry) => (
          <div key={String(entry.dataKey)} className="flex items-center gap-2">
            <span
              className="h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: entry.color ?? colors?.[String(entry.dataKey)] ?? '#94a3b8' }}
            />
            <span className="font-medium text-slate-700">{entry.name ?? entry.dataKey}</span>
            <span>{formatQty(Number(entry.value ?? 0))}{suffix ?? ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DashboardLineChart({
  series,
  months,
  valueSuffix = ' kg',
  legendSuffix,
}: {
  series: YearSeries[];
  months: readonly MonthDef[];
  valueSuffix?: string;
  legendSuffix?: string;
}) {
  const data = useMemo(() => buildRows(series, months), [months, series]);
  const colors = useMemo(
    () => Object.fromEntries(series.map((entry, index) => [String(entry.year), YEAR_COLORS[index]])),
    [series]
  );

  return (
    <div>
      <div className="mb-3">
        <YearLegend series={series} />
      </div>
      <div className="h-[250px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart data={data} margin={{ top: 8, right: 10, left: -4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="4 6" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={34} />
            <Tooltip content={<ChartTooltip suffix={valueSuffix} colors={colors} />} />
            {series.map((entry, index) => (
              <Line
                key={entry.year}
                type="monotone"
                dataKey={String(entry.year)}
                stroke={YEAR_COLORS[index]}
                strokeWidth={3}
                dot={{ r: 4, stroke: '#fff', strokeWidth: 1.5 }}
                activeDot={{ r: 6 }}
                name={String(entry.year)}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
      <MatrixLegend series={series} months={months} suffix={legendSuffix ?? valueSuffix} />
    </div>
  );
}

export function DashboardBarChart({
  series,
  months,
  valueSuffix = '',
}: {
  series: YearSeries[];
  months: readonly MonthDef[];
  valueSuffix?: string;
}) {
  const data = useMemo(() => buildRows(series, months), [months, series]);
  const colors = useMemo(
    () => Object.fromEntries(series.map((entry, index) => [String(entry.year), YEAR_COLORS[index]])),
    [series]
  );

  return (
    <div>
      <div className="mb-3">
        <YearLegend series={series} />
      </div>
      <div className="h-[260px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsBarChart data={data} margin={{ top: 8, right: 10, left: -4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="4 6" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} width={34} />
            <Tooltip content={<ChartTooltip suffix={valueSuffix} colors={colors} />} />
            {series.map((entry, index) => (
              <Bar
                key={entry.year}
                dataKey={String(entry.year)}
                radius={[5, 5, 0, 0]}
                name={String(entry.year)}
                barSize={18}
              >
                {data.map((_, dataIndex) => (
                  <Cell key={`${entry.year}-${dataIndex}`} fill={YEAR_COLORS[index]} />
                ))}
              </Bar>
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      <MatrixLegend series={series} months={months} suffix={valueSuffix} />
    </div>
  );
}

